import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "trigger", "input", "panel", "listbox", "chipRow",
    "applyButton", "clearButton", "emptyState", "triggerLabel",
    "sectionLabel", "hint", "hintText"
  ]
  static values = {
    basePath: String,
    preservedParams: { type: String, default: "{}" },
    param: String,
    mode: { type: String, default: "multi" },
    source: { type: String, default: "async" },
    options: { type: String, default: "[]" },
    selected: { type: String, default: "[]" },
    selectedOptions: { type: String, default: "[]" },
    searchUrl: String,
    minChars: { type: Number, default: 2 },
    debounceMs: { type: Number, default: 300 },
    emptyLabel: { type: String, default: "All" },
    label: String,
    countLabel: { type: String, default: "items" },
    fragment: { type: String, default: "" }
  }

  connect() {
    this._committed = this._parseSelected()
    this._draft = new Map()
    this._hydrateDraftFromCommitted()
    this._allOptions = this._parseOptions()
    this._searchResults = []
    this._rosterTotal = 0
    this._activeIndex = -1
    this._searchAbort = null
    this._debounceTimer = null

    this._updateTriggerLabel()
    this._onDocClick = (e) => {
      if (this.panelTarget.classList.contains("hidden")) return
      if (this.element.contains(e.target)) return
      this._close(false)
    }
    this._onKey = (e) => {
      if (e.key === "Escape" && !this.panelTarget.classList.contains("hidden")) {
        this._close(false)
      }
    }
    this._onPanelKeydown = (e) => this._trapPanelFocus(e)
    document.addEventListener("click", this._onDocClick, true)
    document.addEventListener("keydown", this._onKey)
    this.panelTarget.addEventListener("keydown", this._onPanelKeydown)
  }

  disconnect() {
    document.removeEventListener("click", this._onDocClick, true)
    document.removeEventListener("keydown", this._onKey)
    this.panelTarget.removeEventListener("keydown", this._onPanelKeydown)
    this._abortSearch()
    if (this._debounceTimer) clearTimeout(this._debounceTimer)
  }

  togglePanel(event) {
    event?.stopPropagation()
    if (this.panelTarget.classList.contains("hidden")) {
      this._open()
    } else {
      this._close(false)
    }
  }

  searchInput() {
    if (this.sourceValue === "static") {
      this._runStaticFilter()
      return
    }
    if (this.sourceValue !== "async") return
    if (this._debounceTimer) clearTimeout(this._debounceTimer)
    this._debounceTimer = setTimeout(() => this._runSearch(), this.debounceMsValue)
  }

  inputKeydown(event) {
    const key = event.key
    if (key === "ArrowDown") {
      event.preventDefault()
      this._moveActive(1)
    } else if (key === "ArrowUp") {
      event.preventDefault()
      this._moveActive(-1)
    } else if (key === "Enter") {
      event.preventDefault()
      if (this._activeIndex >= 0 && this._searchResults[this._activeIndex]) {
        this._toggleDraft(this._searchResults[this._activeIndex])
      }
    } else if (key === "Escape") {
      event.preventDefault()
      this._close(false)
    }
  }

  selectOption(event) {
    const li = event.currentTarget.closest("[role='option']") || event.currentTarget
    const value = li.dataset.value
    const label = li.dataset.label
    const meta = li.dataset.meta || ""
    if (!value) return
    this._toggleDraft({ value, label, meta })
  }

  removeChip(event) {
    event.stopPropagation()
    const value = event.currentTarget.dataset.value
    if (value) this._draft.delete(value)
    this._hideEmptyState()
    this._renderChips()
    this._renderListbox()
  }

  apply() {
    this._committed = Array.from(this._draft.keys())
    this._navigateWithSelection(this._committed)
    this._close(true)
  }

  clear() {
    this._draft.clear()
    this._committed = []
    this._navigateWithSelection([])
    this._close(true)
  }

  _open() {
    this._draft = new Map()
    this._hydrateDraftFromCommitted()
    this.panelTarget.classList.remove("hidden")
    this.triggerTarget.setAttribute("aria-expanded", "true")
    this._activeIndex = -1
    this._renderChips()
    this._hideEmptyState()
    if (this.sourceValue === "async") {
      this._fetchDefaultResults()
    } else if (this.sourceValue === "static") {
      this._searchResults = [...this._allOptions]
      this._renderListbox()
    } else {
      this._searchResults = []
      this._renderListbox()
    }
    requestAnimationFrame(() => this.inputTarget.focus())
  }

  _close(committed) {
    this.panelTarget.classList.add("hidden")
    this.triggerTarget.setAttribute("aria-expanded", "false")
    this.inputTarget.value = ""
    this._searchResults = []
    this._activeIndex = -1
    this._abortSearch()
    this._hideEmptyState()
    this._hideRosterMeta()
    if (!committed) {
      this._draft = new Map()
      this._hydrateDraftFromCommitted()
    }
    this._updateTriggerLabel()
    this.triggerTarget.focus()
  }

  _hydrateDraftFromCommitted() {
    const options = this._parseSelectedOptions()
    this._committed.forEach((value) => {
      const opt = options.find((o) => o.value === value)
      if (opt) {
        this._draft.set(value, opt)
      } else {
        this._draft.set(value, { value, label: value, meta: "" })
      }
    })
  }

  _parseSelected() {
    try {
      const parsed = JSON.parse(this.selectedValue || "[]")
      return Array.isArray(parsed) ? parsed.map(String) : []
    } catch {
      return []
    }
  }

  _parseSelectedOptions() {
    try {
      const parsed = JSON.parse(this.selectedOptionsValue || "[]")
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }

  _parseOptions() {
    try {
      const parsed = JSON.parse(this.optionsValue || "[]")
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }

  async _fetchDefaultResults() {
    if (this.sourceValue !== "async" || !this.searchUrlValue) return

    this._abortSearch()
    this._searchAbort = new AbortController()

    try {
      const url = new URL(this.searchUrlValue, window.location.origin)
      // Forward the page's active timeframe so "recently active" reflects the
      // currently-viewed date range (server falls back to the session-remembered
      // selection when these are absent).
      const pageParams = new URLSearchParams(window.location.search)
      for (const key of ["period", "start_date", "end_date"]) {
        const val = pageParams.get(key)
        if (val) url.searchParams.set(key, val)
      }
      let resp = await fetch(url.toString(), {
        signal: this._searchAbort.signal,
        headers: { Accept: "application/json" }
      })

      if (resp.status === 304) {
        resp = await fetch(url.toString(), {
          signal: this._searchAbort.signal,
          headers: { Accept: "application/json" },
          cache: "reload"
        })
      }

      if (!resp.ok) return
      const data = await resp.json()
      this._searchResults = Array.isArray(data.options) ? data.options : []
      this._rosterTotal = Number.isFinite(data.total) ? data.total : this._searchResults.length
      this._activeIndex = -1
      this._renderListbox()
      this._renderRosterMeta()
      this._hideEmptyState()
    } catch (e) {
      if (e.name !== "AbortError") console.error("Combobox default roster failed:", e)
    }
  }

  async _runSearch() {
    const q = this.inputTarget.value.trim()
    if (q.length < this.minCharsValue) {
      await this._fetchDefaultResults()
      return
    }

    this._abortSearch()
    this._searchAbort = new AbortController()

    try {
      const url = new URL(this.searchUrlValue, window.location.origin)
      url.searchParams.set("q", q)
      const resp = await fetch(url.toString(), {
        signal: this._searchAbort.signal,
        headers: { Accept: "application/json" }
      })
      if (!resp.ok) return
      const data = await resp.json()
      this._searchResults = Array.isArray(data.options) ? data.options : []
      this._activeIndex = -1
      this._renderListbox()
      this._hideRosterMeta()
      if (this._searchResults.length === 0) {
        this._showEmptyState("No results found")
      } else {
        this._hideEmptyState()
      }
    } catch (e) {
      if (e.name !== "AbortError") console.error("Combobox search failed:", e)
    }
  }

  _runStaticFilter() {
    const q = this.inputTarget.value.trim().toLowerCase()
    if (q === "") {
      this._searchResults = [...this._allOptions]
    } else {
      this._searchResults = this._allOptions.filter((opt) => {
        const label = (opt.label || "").toLowerCase()
        const value = (opt.value || "").toLowerCase()
        return label.includes(q) || value.includes(q)
      })
    }
    this._activeIndex = -1
    this._renderListbox()
    if (this._searchResults.length === 0) {
      this._showEmptyState("No results found")
    } else {
      this._hideEmptyState()
    }
  }

  _abortSearch() {
    if (this._searchAbort) {
      this._searchAbort.abort()
      this._searchAbort = null
    }
  }

  _toggleDraft(option) {
    if (this.modeValue === "single") {
      this._draft.clear()
      this._draft.set(option.value, option)
      this._committed = [option.value]
      this._navigateWithSelection(this._committed)
      this._close(true)
      return
    }

    if (this._draft.has(option.value)) {
      this._draft.delete(option.value)
      this._hideEmptyState()
    } else {
      if (this._draft.size >= 10) {
        this._showEmptyState(`You can select up to 10 ${this.countLabelValue}`)
        return
      }
      this._draft.set(option.value, option)
      this._hideEmptyState()
    }
    this._renderChips()
    this._renderListbox()
  }

  _renderChips() {
    if (!this.hasChipRowTarget) return
    const row = this.chipRowTarget
    while (row.firstChild) row.removeChild(row.firstChild)

    if (this.modeValue === "single") return

    this._draft.forEach((opt) => {
      const chip = document.createElement("span")
      chip.className = "orpheus-combobox__chip"
      const label = document.createElement("span")
      label.textContent = opt.label
      chip.appendChild(label)
      const btn = document.createElement("button")
      btn.type = "button"
      btn.className = "orpheus-combobox__chip-remove"
      btn.dataset.value = opt.value
      btn.setAttribute("aria-label", "Remove " + opt.label)
      btn.textContent = "×"
      btn.addEventListener("click", (e) => {
        e.stopPropagation()
        this._draft.delete(opt.value)
        this._hideEmptyState()
        this._renderChips()
        this._renderListbox()
      })
      chip.appendChild(btn)
      row.appendChild(chip)
    })
  }

  _renderListbox() {
    if (!this.hasListboxTarget) return
    const list = this.listboxTarget
    while (list.firstChild) list.removeChild(list.firstChild)

    this._searchResults.forEach((opt, idx) => {
      const li = document.createElement("li")
      li.role = "option"
      li.className = "orpheus-combobox__option"
      li.dataset.value = opt.value
      li.dataset.label = opt.label
      li.dataset.meta = opt.meta || ""
      li.setAttribute("aria-selected", this._draft.has(opt.value) ? "true" : "false")
      li.id = this.listboxTarget.id + "-opt-" + idx
      if (idx === this._activeIndex) li.dataset.active = "true"
      li.addEventListener("mouseenter", () => this._setActiveIndex(idx))
      li.addEventListener("click", (e) => {
        e.stopPropagation()
        this._toggleDraft(opt)
      })

      const labelSpan = document.createElement("span")
      labelSpan.textContent = opt.label
      li.appendChild(labelSpan)

      if (opt.meta) {
        const metaSpan = document.createElement("span")
        metaSpan.className = "block text-xs text-gray-500"
        metaSpan.textContent = opt.meta
        li.appendChild(metaSpan)
      }

      list.appendChild(li)
    })

    this._updateActivedescendant()
  }

  _setActiveIndex(idx) {
    if (this._searchResults.length === 0) {
      this._activeIndex = -1
      this._updateActivedescendant()
      return
    }
    const clamped = Math.max(0, Math.min(this._searchResults.length - 1, idx))
    if (this._activeIndex === clamped) return

    const options = this.listboxTarget.querySelectorAll('[role="option"]')
    if (this._activeIndex >= 0 && options[this._activeIndex]) {
      delete options[this._activeIndex].dataset.active
    }
    this._activeIndex = clamped
    if (options[clamped]) {
      options[clamped].dataset.active = "true"
      options[clamped].scrollIntoView({ block: "nearest" })
    }
    this._updateActivedescendant()
  }

  _moveActive(delta) {
    if (this._searchResults.length === 0) return
    if (this._activeIndex === -1 && delta > 0) {
      this._setActiveIndex(0)
      return
    }
    if (this._activeIndex === -1) return
    this._setActiveIndex(this._activeIndex + delta)
  }

  _updateActivedescendant() {
    if (this._activeIndex >= 0 && this._searchResults[this._activeIndex]) {
      this.inputTarget.setAttribute("aria-activedescendant", this.listboxTarget.id + "-opt-" + this._activeIndex)
    } else {
      this.inputTarget.removeAttribute("aria-activedescendant")
    }
  }

  // Default-roster only: label the list as a subset and point to search so the
  // scrollbar never implies "more below" over a hidden cap. Hidden during search
  // and for static filters (which show their whole list).
  _renderRosterMeta() {
    if (!this.hasSectionLabelTarget) return
    const count = this._searchResults.length
    if (count === 0) {
      this._hideRosterMeta()
      return
    }

    const total = Number.isFinite(this._rosterTotal) ? this._rosterTotal : count
    const truncated = total > count

    while (this.sectionLabelTarget.firstChild) this.sectionLabelTarget.removeChild(this.sectionLabelTarget.firstChild)
    const label = document.createElement("span")
    label.textContent = truncated ? "Recently active" : "All " + this.countLabelValue
    this.sectionLabelTarget.appendChild(label)
    if (!truncated) {
      const countEl = document.createElement("span")
      countEl.className = "orpheus-combobox__section-count"
      countEl.textContent = total.toLocaleString()
      this.sectionLabelTarget.appendChild(countEl)
    }
    this.sectionLabelTarget.hidden = false

    if (this.hasHintTarget) {
      if (truncated && this.hasHintTextTarget) {
        while (this.hintTextTarget.firstChild) this.hintTextTarget.removeChild(this.hintTextTarget.firstChild)
        this.hintTextTarget.appendChild(document.createTextNode("Type to search all "))
        const strong = document.createElement("b")
        strong.textContent = total.toLocaleString()
        this.hintTextTarget.appendChild(strong)
        this.hintTextTarget.appendChild(document.createTextNode(" " + this.countLabelValue))
        this.hintTarget.hidden = false
      } else {
        this.hintTarget.hidden = true
      }
    }
  }

  _hideRosterMeta() {
    if (this.hasSectionLabelTarget) this.sectionLabelTarget.hidden = true
    if (this.hasHintTarget) this.hintTarget.hidden = true
  }

  _showEmptyState(message) {
    if (!this.hasEmptyStateTarget) return
    this.emptyStateTarget.textContent = message
    this.emptyStateTarget.hidden = false
  }

  _hideEmptyState() {
    if (!this.hasEmptyStateTarget) return
    this.emptyStateTarget.textContent = ""
    this.emptyStateTarget.hidden = true
  }

  _updateTriggerLabel() {
    if (!this.hasTriggerLabelTarget) return
    const labels = this._committed.map((v) => {
      const draftOpt = this._draft.get(v)
      if (draftOpt?.label) return draftOpt.label
      const opt = this._parseSelectedOptions().find((o) => o.value === v)
      return opt ? opt.label : v
    })

    if (labels.length === 0) {
      this.triggerLabelTarget.textContent = this.labelValue || this.emptyLabelValue
    } else if (labels.length === 1) {
      this.triggerLabelTarget.textContent = labels[0]
    } else if (labels.length === 2) {
      this.triggerLabelTarget.textContent = labels.join(", ")
    } else {
      this.triggerLabelTarget.textContent = labels.length + " " + this.countLabelValue
    }
  }

  _navigateWithSelection(values) {
    const params = this._preservedParams()
    // Changing a filter resets pagination. `page` is the primary paginator;
    // `dev_page` is the per-developer breakdown's secondary paginator (a no-op
    // delete on pages that don't use it).
    params.delete("page")
    params.delete("dev_page")
    params.delete(this.paramValue)
    params.delete(this.paramValue + "[]")
    if (this.modeValue === "single") {
      if (values.length > 0) params.set(this.paramValue, values[0])
    } else {
      values.forEach((v) => params.append(this.paramValue + "[]", v))
    }
    this._navigate(params)
  }

  _navigate(params) {
    const base = this.basePathValue
    if (!base || !base.startsWith("/orpheus/")) {
      console.error("Combobox: invalid basePath", base)
      return
    }
    const query = params.toString()
    let url = query ? `${base}?${query}` : base
    if (this.fragmentValue) url += `#${this.fragmentValue}`
    if (window.Turbo) {
      Turbo.visit(url)
    } else {
      window.location.href = url
    }
  }

  _preservedParams() {
    // Read the LIVE URL, not the server-baked `preservedParams` snapshot taken
    // at render time. Any filter/search changed client-side after render — e.g.
    // a debounced search that updates the URL via history.replaceState + a
    // partial AJAX swap without re-rendering this combobox — is absent from the
    // baked snapshot and would otherwise be silently dropped on the next filter
    // click. The URL already carries every other filter in the exact param
    // shape the server emits (scalars and repeated `key[]` for multi-select),
    // so we keep all of them and only strip this combobox's own param (and
    // pagination) in _navigateWithSelection. The server re-validates every
    // param on the next request, so forwarding the live set is safe.
    return new URLSearchParams(window.location.search)
  }

  _focusableInPanel() {
    const selector = 'input:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    return Array.from(this.panelTarget.querySelectorAll(selector))
      .filter((el) => el.offsetParent !== null && !el.hidden)
  }

  _trapPanelFocus(event) {
    if (event.key !== "Tab" || this.panelTarget.classList.contains("hidden")) return

    const focusable = this._focusableInPanel()
    if (focusable.length === 0) return

    const first = focusable[0]
    const last = focusable[focusable.length - 1]

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }
}
